import * as React from 'react';
import { ArrowLeft, ArrowRight, Check, Route } from 'lucide-react';
import { ROLE_LABELS, type Role } from '@cfl/shared';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/cargofl-ui';
import { visibleNavSections, type NavItem, type NavVisibilityContext } from '@/lib/nav';
import { useCurrentUser } from '@/lib/session';

/**
 * First-login tour: what this person may actually do here.
 *
 * The middle steps are the *same* `visibleNavSections` the sidebar renders, so the
 * tour can never promise a destination the role cannot open, and a nav item added
 * later shows up here without anyone remembering to. Copy per destination lives on
 * the nav item itself (`NavItem.description`) rather than in a second table keyed
 * by href, which is how the two would have drifted.
 */

interface TourStep {
  title: string;
  body: string;
  items: NavItem[];
}

/** What the role *is*, in one line. Keyed on the permission tier, not the job title. */
const ROLE_INTRO: Record<Role, string> = {
  EMPLOYEE:
    'You hold your own goals, write your own quarterly self-assessment, and see the recognition you have given and received. Everything below is yours.',
  MANAGER:
    'Alongside your own goals and assessments, you review the people who report to you — and only those people.',
  DEPT_HEAD:
    'Alongside your own goals and assessments, you get read-only oversight of your whole department. You can see everyone’s numbers; you are not a gate in the review chain.',
  HR: 'You run the cycle: onboarding, quarters, goal assignments and analytics — and you are stage 3 of every review in the company.',
  SUPER_ADMIN:
    'Everything HR can do, plus the final management sign-off, the organization structure and the audit log.',
};

/** Why a whole section is on this person's screen. Keyed by NavSection.label. */
const SECTION_BODY: Record<string, string> = {
  'My work': 'Yours no matter what role you hold — every person in the company has these.',
  'My team':
    'You see this because people report to you, not because of your role. The manager stage of a review is always that employee’s own reporting manager — nobody else can supply it, and no role setting changes that.',
  Department: 'Oversight only. Look at anything, approve nothing.',
  HR: 'Running the cycle on behalf of everyone else.',
  Management: 'The last word: final ratings, the org structure, and the trail of who changed what.',
};

function tourSteps(ctx: NavVisibilityContext, firstName: string): TourStep[] {
  return [
    {
      title: `Welcome, ${firstName}`,
      body: `You are signed in as ${ROLE_LABELS[ctx.role]}. ${ROLE_INTRO[ctx.role]}`,
      items: [],
    },
    ...visibleNavSections(ctx).map((section) => ({
      title: section.label,
      body: SECTION_BODY[section.label] ?? '',
      items: section.items,
    })),
    {
      title: 'How an assessment becomes a rating',
      body: 'Four stages, each one blocking the next: your self-assessment, then your reporting manager, then HR, then Management. Nothing skips a stage, and the rating is only final once Management signs it off.',
      items: [],
    },
  ];
}

const SEEN_KEY = 'cfl.tour.seen.v1';

/** localStorage throws in some locked-down browsers; a tour is never worth a white screen. */
function seen(userId: string): boolean {
  try {
    return localStorage.getItem(`${SEEN_KEY}:${userId}`) === '1';
  } catch {
    return true;
  }
}

function markSeen(userId: string): void {
  try {
    localStorage.setItem(`${SEEN_KEY}:${userId}`, '1');
  } catch {
    /* ignore */
  }
}

/**
 * Opens itself once per person, then never again unless they ask for it from the
 * user menu. Marked seen on open rather than on finish, so dismissing it counts.
 */
export function useWelcomeTour() {
  const user = useCurrentUser();
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (seen(user.id)) return;
    markSeen(user.id);
    setOpen(true);
  }, [user.id]);

  return { open, setOpen };
}

export function WelcomeTour({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const user = useCurrentUser();
  const [index, setIndex] = React.useState(0);

  const steps = React.useMemo(
    () =>
      tourSteps(
        { role: user.role, hasReports: user.hasReports },
        user.name.trim().split(/\s+/)[0] ?? user.name,
      ),
    [user.role, user.hasReports, user.name],
  );

  // Restart from the top each time it opens, including a re-run from the user menu.
  React.useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  const step = steps[Math.min(index, steps.length - 1)];
  const last = index >= steps.length - 1;

  // Unreachable — tourSteps always returns the welcome and closing steps.
  if (!step) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <Route className="size-3.5" />
            Quick tour · step {index + 1} of {steps.length}
          </div>
          <DialogTitle>{step.title}</DialogTitle>
          <DialogDescription>{step.body}</DialogDescription>
        </DialogHeader>

        {step.items.length > 0 && (
          <ul className="space-y-3">
            {step.items.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.href} className="flex gap-3">
                  <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <Icon className="size-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{item.title}</div>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {last ? 'Close' : 'Skip tour'}
          </Button>
          <div className="flex gap-2">
            {index > 0 && (
              <Button variant="outline" onClick={() => setIndex((i) => i - 1)}>
                <ArrowLeft className="size-4" />
                Back
              </Button>
            )}
            <Button onClick={() => (last ? onOpenChange(false) : setIndex((i) => i + 1))}>
              {last ? (
                <>
                  <Check className="size-4" />
                  Got it
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="size-4" />
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
